import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import isMobile from 'ismobilejs';
import scroll from 'scroll';

import CarouselItem from './CarouselItem';
import CarouselArrow from './CarouselArrow';
import { executeAction } from '../utils/message';

import { IS_FIREFOX } from '../constants/browsers';
import { SCREEN_SIZES } from '../../../shared/js/constants/sizes';
import { MESSAGE_ITEM, AVATAR_CONTAINER, SCROLLBAR } from '../constants/dimensions';
import { showAvatarForMessage, hideAvatarForMessage } from '../actions/avatar';

const ITEM_MARGIN = 5;
const ITEM_TOTAL_WIDTH = MESSAGE_ITEM.WIDTH + ITEM_MARGIN;

const styles = {
    carouselContainer: {
        overflow: 'hidden',
    },
    scrollContainer: {
        display: 'flex',
        whiteSpace: 'nowrap',
        overflowX: 'scroll',
        paddingLeft: `${AVATAR_CONTAINER.WIDTH}px`,
    },
    maskedContainer: {
        marginBottom: '-5px',
    },
    mobileScroll: {
        paddingBottom: `${SCROLLBAR.HEIGHT}px`,
        marginBottom: '-10px',
    },
    firefoxScroll: {
        paddingBottom: '5px',
        marginBottom: `-${SCROLLBAR.HEIGHT}px`,
        scrollbarWidth: 'none',
    },
};

export default class CarouselMessage extends PureComponent {
    static propTypes = {
        _id: PropTypes.string.isRequired,
        dispatch: PropTypes.func.isRequired,
        items: PropTypes.array.isRequired,
        linkColor: PropTypes.string,
        displaySettings: PropTypes.object,
    };

    currentIndex = 0;
    isScrolling = false;
    estimateIndex = 0;
    scrollDistance = 0;

    state = {
        showPreviousArrow: false,
        showNextArrow: true,
        carouselContainerHeight: 0,
    };

    componentDidMount() {
        this.setState({
            showNextArrow: this._getShowNextArrow(),
            carouselContainerHeight: this._container.clientHeight,
        });
        window.addEventListener('resize', this._updateArrows);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._updateArrows);
    }

    _getShowPreviousArrow() {
        return this.scrollDistance !== 0;
    }

    _getShowNextArrow() {
        const { items } = this.props;
        // For some reason clientWidth is not available at that time if it's a small screen(big carousel)
        const clientWidth =
            window.outerWidth <= SCREEN_SIZES.xs.maxWidth ? window.outerWidth : this._container.clientWidth;

        return (
            items.length * ITEM_TOTAL_WIDTH - ITEM_MARGIN + AVATAR_CONTAINER.WIDTH > clientWidth + this.scrollDistance
        );
    }

    _updateArrows = () => {
        const { items } = this.props;
        const firstItemIndex = 0;
        const lastItemIndex = items.length - 1;

        const indexToUpdate = [firstItemIndex, firstItemIndex + 1, lastItemIndex - 1, lastItemIndex];

        if (indexToUpdate.includes(this.currentIndex)) {
            this.setState({
                showPreviousArrow: this._getShowPreviousArrow(),
                showNextArrow: this._getShowNextArrow(),
            });
        }
    };

    _scrollToIndex(targetIndex) {
        scroll.left(this._container, ITEM_TOTAL_WIDTH * targetIndex, () => {
            this.isScrolling = false;
        });

        this.estimateIndex = targetIndex;
        this.currentIndex = targetIndex;
        this.isScrolling = true;
    }

    onPrevious = () => {
        const { currentIndex, estimateIndex, isScrolling } = this;
        let targetIndex = Math.floor(estimateIndex);

        if (isScrolling) {
            return;
        }

        if (estimateIndex === currentIndex) {
            targetIndex = targetIndex - 1;
        }

        if (targetIndex >= 0) {
            this._scrollToIndex(targetIndex);
        }
    };

    onNext = () => {
        const { items } = this.props;
        const { currentIndex, estimateIndex, isScrolling } = this;
        let targetIndex = Math.ceil(estimateIndex);

        if (isScrolling) {
            return;
        }

        if (estimateIndex === currentIndex) {
            targetIndex = targetIndex + 1;
        }

        if (targetIndex <= items.length - 1) {
            this._scrollToIndex(targetIndex);
        }
    };

    onItemClick = (defaultAction) => {
        return () => {
            const { dispatch } = this.props;
            executeAction(dispatch, defaultAction);
        };
    };

    onScroll = (e) => {
        this.scrollDistance = e.target.scrollLeft;

        if (this.avatarHidden && this.scrollDistance <= AVATAR_CONTAINER.WIDTH) {
            this.props.dispatch(showAvatarForMessage(this.props._id));
            this.avatarHidden = false;
        } else if (!this.avatarHidden && this.scrollDistance > AVATAR_CONTAINER.WIDTH) {
            this.props.dispatch(hideAvatarForMessage(this.props._id));
            this.avatarHidden = true;
        }

        const estimateIndex = this.scrollDistance / ITEM_TOTAL_WIDTH;

        this.estimateIndex = estimateIndex;
        this.currentIndex = Math.ceil(estimateIndex);
        this._updateArrows();
    };

    _buildCarouselItems() {
        const carouselItems = [];
        const { items, linkColor, displaySettings, dispatch } = this.props;

        items.forEach((item, index) => {
            const isFirstItem = index === 0;
            const isLastItem = index === items.length - 1;

            const carouselItem = (
                <CarouselItem
                    key={item._id}
                    {...item}
                    dispatch={dispatch}
                    isFirstItem={isFirstItem}
                    isLastItem={isLastItem}
                    displaySettings={displaySettings}
                    linkColor={linkColor}
                />
            );

            carouselItems.push(carouselItem);
        });

        return carouselItems;
    }

    render() {
        const { items } = this.props;
        const { showPreviousArrow, showNextArrow } = this.state;
        const isAnyMobile = isMobile().any;

        const isArrowEnabled = !isAnyMobile && items.length > 1;
        let carouselContainerStyle = { ...styles.carouselContainer };
        let scrollContainerStyle = { ...styles.scrollContainer };

        if (isAnyMobile) {
            carouselContainerStyle = {
                ...carouselContainerStyle,
                ...styles.maskedContainer,
            };

            scrollContainerStyle = {
                ...scrollContainerStyle,
                ...styles.mobileScroll,
            };
        }

        if (IS_FIREFOX) {
            carouselContainerStyle = {
                ...carouselContainerStyle,
                ...styles.maskedContainer,
            };
            carouselContainerStyle.height = `${this.state.carouselContainerHeight}px`;

            scrollContainerStyle = {
                ...scrollContainerStyle,
                ...styles.firefoxScroll,
            };
        }

        return (
            <div style={carouselContainerStyle} className="carousel-container">
                <div
                    ref={(c) => (this._container = c)}
                    className="scroll-container"
                    style={scrollContainerStyle}
                    onScroll={this.onScroll}
                >
                    {isArrowEnabled ? <CarouselArrow isHidden={!showPreviousArrow} onClick={this.onPrevious} /> : null}
                    {this._buildCarouselItems()}
                    {isArrowEnabled ? (
                        <CarouselArrow isHidden={!showNextArrow} isNext={true} onClick={this.onNext} />
                    ) : null}
                </div>
            </div>
        );
    }
}
